import billingRestrictionsOverrideMutation from "@/graphql/mutations/billingRestrictionsOverrideMutation.graphql";
import ClientSafeException from "@/exceptions/clientSafeException";
import extendDemoDataResetWindowMutation from "@/graphql/mutations/extendDemoDataResetWindowMutation.graphql";
import fileUploadMutation from "@/graphql/mutations/fileUploadMutation.graphql";
import forceSyncBillingMutation from "@/graphql/mutations/billingSyncMutation.graphql";
import locationDeleteMutation from "@/graphql/mutations/locationDeleteMutation.graphql";
import mobileAppSettingsUpdateMutation from "@/graphql/mutations/mobileAppSettingsUpdateMutation.graphql";
import optixAccountUpdateMutation from "@/graphql/mutations/optixAccountUpdateMutation.graphql";
import optixCustomerQuery from "@/graphql/queries/optixCustomerQuery.graphql";
import optixMembershipsBackofficeQuery from "@/graphql/queries/optixMembershipsBackofficeQuery.graphql";
import organizationAdminGrantMutation from "@/graphql/mutations/organizationAdminGrantMutation.graphql";
import organizationDetailsQuery from "@/graphql/queries/organizationDetailsQuery.graphql";
import organizationResetTrialMutation from "@/graphql/mutations/organizationResetTrialMutation.graphql";
import organizationSetCustomerMutation from "@/graphql/mutations/organizationSetCustomerMutation.graphql";
import organizationUnsetCustomerMutation from "@/graphql/mutations/organizationUnsetCustomerMutation.graphql";
import organizationUpdateFeaturesMutation from "@/graphql/mutations/organizationUpdateFeaturesMutation.graphql";
import { graphQLClient } from "@/utils/graphQLClient";

const ADMIN_ROLE_IDS = {
  OWNER: 1,
  MANAGER: 2,
  CLIENT_SERVICE: 3,
  RECEPTION: 4,
};

const LOCATION_STATUS_IDS = {
  ACTIVE: 1,
  DELETED: 2,
  HIDDEN: 3,
};

function mapOrganizationDetailsResponse(organization) {
  let { trial_expires } = organization.optix_account;
  let days_remaining = Math.ceil((trial_expires - Date.now() / 1000) / 86400);

  return {
    ...organization,
    venue_id: organization.organization_id,
    admins: organization.backoffice_admins.map(admin => ({
      admin_id: admin.user.user_id,
      user_id: admin.user.user_id,
      role_id: ADMIN_ROLE_IDS[admin.admin_role],
      name: admin.user.name,
      surname: admin.user.surname,
      email: admin.user.email,
      image: admin.user.image,
      expiration_timestamp: admin.expiration_timestamp,
    })),
    locations: organization.locations.data.map(location => ({
      ...location,
      status: LOCATION_STATUS_IDS[location.status],
    })),
    membership: {
      ...organization.optix_account,
      venue_id: organization.organization_id,
      subdomain: organization.subdomain,
      created: organization.optix_account.created_timestamp,
      days_remaining: Math.max(days_remaining, 0),
    },
  };
}

export default {
  namespaced: false,
  state: {
    organization: null,
    customer: null,
  },
  mutations: {
    MUTATE_ORGANIZATION(state, organization) {
      state.organization = organization;
    },
    MUTATE_MEMBERSHIP(state, membership) {
      state.organization.membership = membership;
    },
    MUTATE_CUSTOMER(state, customer) {
      state.customer = customer;
    },
    MUTATE_MEMBERSHIP_SETTING(state, setting) {
      if (state.organization && state.organization.membership) {
        state.organization.membership = {
          ...state.organization.membership,
          ...setting,
        };
      }
    },
  },
  getters: {
    getOrganization: state => state.organization,
    getCustomer: state => state.customer,
  },
  actions: {
    // `membership_type_id`/`has_signed_up` (still sent by OrganizationList.vue's
    // "Type" filter for the subscriber/trial-distinguishing options) have no
    // v2 equivalent here and are silently ignored — same as today's v1
    // adapter, which already dropped them before proxying to this exact query
    // (Actions/backoffice/optix/memberships.php only forwards status/search).
    // Not a regression introduced by this migration, just not re-fixed by it.
    async requestOrganizationList(
      context,
      { offset, limit, search_v2, status }
    ) {
      let { optixMembershipsBackoffice } = await graphQLClient.request(
        optixMembershipsBackofficeQuery,
        {
          page: Math.floor((offset || 0) / Math.max(limit || 1, 1)) + 1,
          limit,
          search: search_v2,
          status: status ? [].concat(status) : null,
        }
      );

      return {
        total: optixMembershipsBackoffice.total,
        accounts: optixMembershipsBackoffice.data.map(membership => ({
          venue_id: membership.organization
            ? membership.organization.organization_id
            : null,
          venue_name: membership.organization
            ? membership.organization.name
            : null,
          country: membership.organization
            ? membership.organization.country
            : null,
          city: membership.organization ? membership.organization.city : null,
          subdomain: membership.organization
            ? membership.organization.subdomain
            : null,
          user_id: membership.user ? membership.user.user_id : null,
          user_name: membership.user
            ? `${membership.user.name} ${membership.user.surname}`.trim()
            : null,
          type: membership.membership_type,
          status: membership.status,
          account_status: membership.account_status,
          billing_type: membership.billing_type,
          created: membership.created_timestamp,
        })),
      };
    },
    async requestOrganizationDetails({ commit }, organization_id) {
      let { organizationBackoffice } = await graphQLClient.request(
        organizationDetailsQuery,
        {
          organization_id,
        }
      );

      if (!organizationBackoffice) {
        throw new ClientSafeException("Couldn't get response from API");
      }

      commit(
        "MUTATE_ORGANIZATION",
        mapOrganizationDetailsResponse(organizationBackoffice)
      );
    },
    async requestUpdateMembershipViaV2({ commit, state }, payload) {
      let response = await graphQLClient.request(optixAccountUpdateMutation, {
        organization_id: state.organization.venue_id,
        ...payload,
      });

      commit("MUTATE_MEMBERSHIP", {
        ...state.organization.membership,
        ...response.optixAccountUpdate,
      });
    },
    requestUpdateMobileAppViaV2({ state }, payload) {
      return graphQLClient.request(mobileAppSettingsUpdateMutation, {
        organization_id: state.organization.venue_id,
        ...payload,
      });
    },
    requestFileUpload({ state }, payload) {
      return graphQLClient.request(fileUploadMutation, {
        organization_id: state.organization.venue_id,
        ...payload,
      });
    },
    async requestUpdateOrganizationFeaturesFlag({ commit, state }, setting) {
      let response = await graphQLClient.request(
        organizationUpdateFeaturesMutation,
        {
          organization_id: state.organization.venue_id,
          [setting.flag]: setting.value,
        }
      );

      const updatedAccount =
        response.organizationUpdateFeatures &&
        response.organizationUpdateFeatures.optix_account;
      commit("MUTATE_MEMBERSHIP_SETTING", {
        [setting.flag]: updatedAccount
          ? updatedAccount[setting.flag]
          : setting.value,
      });
    },
    async requestOptixCustomer({ commit, state }, organization_id) {
      if (
        state.customer &&
        state.customer.main_organization.organization_id == organization_id
      ) {
        return;
      }

      let { optixCustomer } = await graphQLClient.request(optixCustomerQuery, {
        organization_id,
      });

      commit("MUTATE_CUSTOMER", optixCustomer);
    },
    async requestLinkStripe({ commit }, { organization_id, customer_id }) {
      let { organizationSetCustomer } = await graphQLClient.request(
        organizationSetCustomerMutation,
        {
          organization_id,
          customer_id,
        }
      );

      if (!organizationSetCustomer) {
        throw new ClientSafeException("Oops... couldn't link Stripe customer");
      }

      commit("MUTATE_CUSTOMER", organizationSetCustomer);
    },
    async requestUnlinkStripe({ commit }, { organization_id, force }) {
      let { organizationUnsetCustomer } = await graphQLClient.request(
        organizationUnsetCustomerMutation,
        {
          organization_id,
          force: force || false,
        }
      );

      if (!organizationUnsetCustomer) {
        throw new ClientSafeException(
          "Oops... couldn't unlink Stripe customer"
        );
      }

      commit("MUTATE_CUSTOMER", null);
    },
    async requestDeleteLocation(context, location_id) {
      let { locationDelete } = await graphQLClient.request(
        locationDeleteMutation,
        {
          location_id,
        }
      );

      if (!locationDelete) {
        throw new ClientSafeException("Oops... couldn't delete location");
      }

      return locationDelete;
    },
    async requestOverrideBillingRestrictions(
      context,
      { optix_billing_customer_id, input }
    ) {
      let { billingRestrictionsOverride } = await graphQLClient.request(
        billingRestrictionsOverrideMutation,
        {
          optix_billing_customer_id,
          input,
        }
      );

      if (!billingRestrictionsOverride) {
        throw new ClientSafeException(
          "Oops... couldn't override billing restrictions"
        );
      }

      return billingRestrictionsOverride.restriction_overrides;
    },
    async requestBillingSync(context, organization_id) {
      let { billingSync } = await graphQLClient.request(
        forceSyncBillingMutation,
        {
          organization_id,
        }
      );

      if (!billingSync) {
        throw new ClientSafeException("Oops... couldn't sync billing");
      }

      return billingSync;
    },
    async requestAddAdmin(context, { user_id, organization_id, minutes = 30 }) {
      let { organizationAdminGrant } = await graphQLClient.request(
        organizationAdminGrantMutation,
        {
          organization_id,
          user_id,
          minutes,
        }
      );

      if (!organizationAdminGrant) {
        throw new ClientSafeException("Couldn't get response from API");
      }
    },
    async requestResetTrial({ state }, { days }) {
      let { organizationResetTrial } = await graphQLClient.request(
        organizationResetTrialMutation,
        {
          organization_id: state.organization.venue_id,
          days,
        }
      );

      if (!organizationResetTrial) {
        throw new ClientSafeException("Couldn't get response from API");
      }
    },
    requestExtendDemoDataResetWindow(context, { organization_id, days }) {
      return graphQLClient.request(extendDemoDataResetWindowMutation, {
        organization_id,
        days,
      });
    },
  },
};
